<script setup>

import {computed, onMounted, ref} from "vue";

const currentAudienceSize = ref(5000);
const expectedConversionRate = ref(5);
const coursePrice = ref(297);
const numberOfCoursesPlanned = ref(3);
const averageCoursesPerCustomer = ref(2);
const expectedRepeatPurchaseRate = ref(30);

const transitioned = ref(false)
const calculating = ref(false);
const seeResult = ref(false);

const results = computed(() => {
    // Initial course sales
    const initialCourseSales = currentAudienceSize.value * (expectedConversionRate.value / 100);

    // Revenue from first course launch
    const revenueFromFirstCourse = initialCourseSales * coursePrice.value;

    // Revenue from multiple courses
    // Customers who buy additional courses = initial sales * repeat purchase rate
    const customersBuyingAdditional = initialCourseSales * (expectedRepeatPurchaseRate.value / 100);
    // Additional courses per customer = (number of courses - 1) * average courses per customer factor
    const additionalCoursesSold = customersBuyingAdditional * (numberOfCoursesPlanned.value - 1) * (averageCoursesPerCustomer.value / numberOfCoursesPlanned.value);
    const revenueFromMultipleCourses = additionalCoursesSold * coursePrice.value;

    // Lifetime value per customer
    const lifetimeValuePerCustomer = coursePrice.value * averageCoursesPerCustomer.value;

    // Total potential revenue
    // This includes initial sales + repeat purchases across all courses
    const totalPotentialRevenue = initialCourseSales * lifetimeValuePerCustomer;

    // Break-even point (assuming some fixed cost)
    // Simplified: break-even = when revenue covers initial investment
    // For this calculation, we'll use a simple assumption that break-even is when revenue = course price * 1 (one sale)
    const breakEvenPoint = Math.ceil(coursePrice.value / (initialCourseSales > 0 ? revenueFromFirstCourse / initialCourseSales : coursePrice.value));

    return {
        initialCourseSales: initialCourseSales.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        initialCourseSalesRaw: initialCourseSales,
        revenueFromFirstCourse: revenueFromFirstCourse.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        revenueFromFirstCourseRaw: revenueFromFirstCourse,
        revenueFromMultipleCourses: revenueFromMultipleCourses.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        revenueFromMultipleCoursesRaw: revenueFromMultipleCourses,
        lifetimeValuePerCustomer: lifetimeValuePerCustomer.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        lifetimeValuePerCustomerRaw: lifetimeValuePerCustomer,
        totalPotentialRevenue: totalPotentialRevenue.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        totalPotentialRevenueRaw: totalPotentialRevenue,
        breakEvenPoint: breakEvenPoint.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
    };
})

const calculateInitial = () => {
    calculating.value = true

    setTimeout(function(){
        seeResult.value = true;

        setTimeout(function() {
            window.scrollBy({
                top: 500,
                behavior: 'smooth'
            });
        }, 300);
    }, 300)
}

onMounted(() => {
    setTimeout(() => {
        transitioned.value = true;
    }, 100);
})

</script>

<template>
    <div>
        <card-basic
            class="transition-all duration-500"
            :class="transitioned ? 'translate-y-0 scale-100' : '-translate-y-4 opacity-0 scale-95'"
        >
            <div class="grid-cols-2 gap-8 md:grid">
                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Current audience size (email list, social followers)
                    </p>
                    <input-number
                        max="10000000"
                        min="0"
                        :step="100"
                        class="mx-auto w-36"
                        v-model:content="currentAudienceSize"
                        :show-step-ui="true"
                        placeholder="Audience size"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Expected conversion rate to course purchase (%)
                    </p>
                    <div class="mx-auto md:w-64">
                        <input-range
                            :content="expectedConversionRate"
                            min="0"
                            max="100"
                            :step="0.1"
                            class="w-full"
                            @update:content="expectedConversionRate = $event"
                        />{{ expectedConversionRate }} %
                    </div>
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Course price
                    </p>
                    <input-number
                        max="100000"
                        min="0"
                        :step="1"
                        class="mx-auto w-36"
                        v-model:content="coursePrice"
                        :show-step-ui="true"
                        placeholder="Course price"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Number of courses planned
                    </p>
                    <input-number
                        max="100"
                        min="1"
                        :step="1"
                        class="mx-auto w-36"
                        v-model:content="numberOfCoursesPlanned"
                        :show-step-ui="true"
                        placeholder="Number of courses"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Average courses purchased per customer
                    </p>
                    <input-number
                        max="100"
                        min="1"
                        :step="0.1"
                        class="mx-auto w-36"
                        v-model:content="averageCoursesPerCustomer"
                        :show-step-ui="true"
                        placeholder="Courses per customer"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Expected repeat purchase rate (%)
                    </p>
                    <div class="mx-auto md:w-64">
                        <input-range
                            :content="expectedRepeatPurchaseRate"
                            min="0"
                            max="100"
                            :step="0.1"
                            class="w-full"
                            @update:content="expectedRepeatPurchaseRate = $event"
                        />{{ expectedRepeatPurchaseRate }} %
                    </div>
                </div>

                <div
                    v-if="! seeResult"
                    class="flex justify-center col-span-2"
                >
                    <button-primary
                        class="justify-center w-48 mx-auto transition-all"
                        :class="calculating ? 'translate-y-8 opacity-0' : ''"
                        @click="calculateInitial"
                    >
                        Calculate
                    </button-primary>
                </div>

                <div
                    v-if="calculating"
                    class="col-span-2 -mx-4 -mt-6 -mb-4 transition-colors duration-1000 text-wlPrimaryContrast md:-mx-6 md:-mb-6 lg:-mx-8 lg:-mb-8"
                    :class="seeResult ? 'bg-wlPrimary' : 'bg-transparent'"
                >
                    <div class="w-full h-12 transform -translate-y-px bg-white rounded-b-wl"></div>
                    <div
                        class="col-span-2 p-4 pt-3 transition-all duration-1000 rounded-b-wl md:p-6 md:pt-4 lg:p-8 lg:pt-5"
                        :class="seeResult ? 'translate-y-0 opacity-100 max-h-screen' : '-translate-y-8 opacity-0 max-h-0'"
                    >
                        <div class="flex mb-8 space-x-4">
                            <div>
                                <i class="mt-1 text-2xl fa-regular fa-chart-line"></i>
                            </div>
                            <div>
                                <p>You would make <strong>{{ results.initialCourseSales }} initial course sales</strong>.</p>

                                <p>Revenue from first course launch: <strong>${{ results.revenueFromFirstCourse }}</strong></p>

                                <p>Revenue from multiple courses: <strong>${{ results.revenueFromMultipleCourses }}</strong></p>

                                <p>Lifetime value per customer: <strong>${{ results.lifetimeValuePerCustomer }}</strong></p>

                                <p>Total potential revenue: <strong>${{ results.totalPotentialRevenue }}</strong></p>

                                <p class="mt-4">
                                    <strong>Break-even point: {{ results.breakEvenPoint }} sales</strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </card-basic>
    </div>
</template>

<style scoped>

</style>

